import type { RadioChangeEvent } from 'antd';
import cn from 'classnames';
import type { ComponentProps, Key, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Radio } from '../radio';
import styles from './radio-pills.module.css';

export type RadioPillsOnChangeEvent<T extends Key = string> = {
  target: { value?: T } & Omit<RadioChangeEvent['target'], 'value'>;
} & Omit<RadioChangeEvent, 'target'>;

export type RadioPillsProperties<T extends Key = string> = {
  items: Array<Item<T>>;
  // TODO: Remove `marginBottom` prop after we refactor layouts in the client
  // Spacing should be handled by the layout, not individual components
  marginBottom?: boolean;
  onChange?: (event: RadioPillsOnChangeEvent<T>) => void;
  value?: T;
} & Omit<ComponentProps<typeof Radio.Group>, 'items' | 'onChange' | 'value'>;

type Item<T> = {
  icon?: ReactNode;
  label: ReactNode;
  value: T;
} & Omit<ComponentProps<typeof Radio.Button>, 'children' | 'value'>;

const RadioPills = <T extends Key = string>({
  items,
  marginBottom = true,
  onChange,
  rootClassName,
  value,
  ...groupProperties
}: RadioPillsProperties<T>) => {
  const { t } = useTranslation();

  return (
    <Radio.Group
      {...groupProperties}
      className={cn(
        styles['radio-pills'],
        { [styles['radio-pills--margin-bottom'] ?? '']: marginBottom },
        rootClassName,
      )}
      onChange={onChange}
      value={value}
    >
      {items.map(({ className: itemClassName, icon, label, size = 'middle', value: itemValue, ...itemProperties }) => (
        <Radio.Button
          className={cn(styles['radio-pills__item'], itemClassName)}
          key={itemValue}
          size={size}
          value={itemValue}
          {...itemProperties}
        >
          {icon ? <span className={styles['radio-pills__icon']}>{icon}</span> : null}
          {typeof label === 'string' ? t(label) : label}
        </Radio.Button>
      ))}
    </Radio.Group>
  );
};

export default RadioPills;
